import axiosInstance from '../config/axios';
import {
  Invoice,
  InvoiceFormValues,
  InvoiceListFilters,
  InvoiceListResponse,
  PODetails,
  POBalance,
  TDSSection,
  RecommendPayload,
  InvoiceAccess,
  WorkflowHistory,
  InvoiceComment,
} from '../types/invoice';

const BASE_URL = '/invoices';

export interface ProjectCodeOption {
  id: string;
  projectCode: string;
  projectType: string;
  displayName?: string;
  projectDetail?: string;
  parentProjectCode?: string;
  parentProjectId?: string;
  source?: string;
}

export const invoiceService = {
  /**
   * Get current user's invoice access
   */
  getAccess: async (): Promise<{ success: boolean; data: InvoiceAccess }> => {
    const response = await axiosInstance.get(`${BASE_URL}/access`);
    return response.data;
  },

  /**
   * Get project codes that have approved POs for invoice creation
   */
  getProjectCodesForInvoice: async (search?: string): Promise<{ success: boolean; data: ProjectCodeOption[] }> => {
    const response = await axiosInstance.get(`${BASE_URL}/project-codes`, {
      params: { search },
    });
    return response.data;
  },

  /**
   * Get POs filtered by project code
   */
  getPOsByProjectCode: async (projectCodeId: string): Promise<{ success: boolean; data: any[] }> => {
    const response = await axiosInstance.get(`${BASE_URL}/project/${projectCodeId}/pos`);
    return response.data;
  },

  /**
   * Get TDS sections master data
   */
  getTDSSections: async (): Promise<{ success: boolean; data: TDSSection[] }> => {
    const response = await axiosInstance.get(`${BASE_URL}/tds-sections`);
    return response.data;
  },

  /**
   * Create a new TDS section
   */
  createTDSSection: async (payload: {
    section_code: string;
    section_name?: string;
    description?: string;
    default_percentage: number;
  }): Promise<{ success: boolean; data: TDSSection; message: string }> => {
    const response = await axiosInstance.post(`${BASE_URL}/tds-sections`, payload);
    return response.data;
  },

  /**
   * Get available POs for invoice creation
   */
  getAvailablePOs: async (): Promise<{ success: boolean; data: any[] }> => {
    const response = await axiosInstance.get(`${BASE_URL}/po-options`);
    return response.data;
  },

  /**
   * Get PO details for auto-population
   */
  getPODetails: async (poId: number): Promise<{ success: boolean; data: PODetails }> => {
    const response = await axiosInstance.get(`${BASE_URL}/po/${poId}/details`);
    return response.data;
  },

  /**
   * Get PO balance (remaining amount)
   */
  getPOBalance: async (
    poId: number,
    options?: { excludeInvoiceId?: number; asOfInvoiceId?: number } | number,
  ): Promise<{ success: boolean; data: POBalance }> => {
    // Backward compatible: allow passing a number as the legacy excludeInvoiceId argument.
    const params: Record<string, number> = {};
    if (typeof options === 'number') {
      params.excludeInvoiceId = options;
    } else if (options) {
      if (options.asOfInvoiceId) params.asOfInvoiceId = options.asOfInvoiceId;
      if (options.excludeInvoiceId) params.excludeInvoiceId = options.excludeInvoiceId;
    }
    const response = await axiosInstance.get(`${BASE_URL}/po/${poId}/balance`, {
      params: Object.keys(params).length ? params : undefined,
    });
    return response.data;
  },

  /**
   * Get next bill sequence for running bills
   */
  getNextBillSequence: async (poId: number): Promise<{ success: boolean; data: { poId: number; nextBillSequence: number } }> => {
    const response = await axiosInstance.get(`${BASE_URL}/po/${poId}/next-bill-sequence`);
    return response.data;
  },

  /**
   * Create a new invoice with optional document upload
   * Sends data as multipart/form-data when files are provided
   */
  createInvoice: async (
    payload: Partial<InvoiceFormValues>,
    files?: File[]
  ): Promise<{ success: boolean; data: Invoice; documents?: any[]; message: string }> => {
    if (files && files.length > 0) {
      const formData = new FormData();
      formData.append('data', JSON.stringify(payload));
      files.forEach((file) => {
        formData.append('files', file);
      });
      const response = await axiosInstance.post(`${BASE_URL}/invoices`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      return response.data;
    } else {
      const response = await axiosInstance.post(`${BASE_URL}/invoices`, payload);
      return response.data;
    }
  },

  /**
   * Get invoice by ID
   */
  getInvoice: async (id: number): Promise<{ success: boolean; data: Invoice }> => {
    const response = await axiosInstance.get(`${BASE_URL}/invoices/${id}`);
    return response.data;
  },

  /**
   * List invoices with filters and pagination
   */
  listInvoices: async (filters?: InvoiceListFilters): Promise<InvoiceListResponse & { success: boolean }> => {
    const response = await axiosInstance.get(`${BASE_URL}/invoices`, { params: filters });
    return response.data;
  },

  /**
   * Update invoice
   */
  updateInvoice: async (
    id: number,
    payload: Partial<InvoiceFormValues>
  ): Promise<{ success: boolean; data: Invoice; message: string }> => {
    const response = await axiosInstance.put(`${BASE_URL}/invoices/${id}`, payload);
    return response.data;
  },

  /**
   * Delete invoice
   */
  deleteInvoice: async (id: number): Promise<{ success: boolean; message: string }> => {
    const response = await axiosInstance.delete(`${BASE_URL}/invoices/${id}`);
    return response.data;
  },

  /**
   * Submit invoice for review
   */
  submitInvoice: async (id: number): Promise<{ success: boolean; data: Invoice; message: string }> => {
    const response = await axiosInstance.post(`${BASE_URL}/invoices/${id}/submit`);
    return response.data;
  },

  /**
   * Review invoice and forward
   */
  reviewInvoice: async (id: number, comments?: string): Promise<{ success: boolean; data: Invoice; message: string }> => {
    const response = await axiosInstance.post(`${BASE_URL}/invoices/${id}/review`, { comments });
    return response.data;
  },

  /**
   * Recommender 1: enter TDS / GST hold / other deductions and forward to
   * Recommender 2 for verification.
   */
  recommendInvoice1: async (
    id: number,
    payload: RecommendPayload
  ): Promise<{ success: boolean; data: Invoice; message: string }> => {
    const response = await axiosInstance.post(`${BASE_URL}/invoices/${id}/recommend-1`, payload);
    return response.data;
  },

  /**
   * Recommender 2: verify (optionally edit) TDS / GST hold / deductions
   * and forward to Approver.
   */
  recommendInvoice2: async (
    id: number,
    payload: RecommendPayload
  ): Promise<{ success: boolean; data: Invoice; message: string }> => {
    const response = await axiosInstance.post(`${BASE_URL}/invoices/${id}/recommend-2`, payload);
    return response.data;
  },

  /**
   * Approve invoice
   */
  approveInvoice: async (id: number, comments?: string): Promise<{ success: boolean; data: Invoice; message: string }> => {
    const response = await axiosInstance.post(`${BASE_URL}/invoices/${id}/approve`, { comments });
    return response.data;
  },

  /**
   * Reject invoice
   */
  rejectInvoice: async (id: number, reason: string): Promise<{ success: boolean; data: Invoice; message: string }> => {
    const response = await axiosInstance.post(`${BASE_URL}/invoices/${id}/reject`, { reason });
    return response.data;
  },

  /**
   * Return invoice for correction
   */
  returnForCorrection: async (id: number, comments: string): Promise<{ success: boolean; data: Invoice; message: string }> => {
    const response = await axiosInstance.post(`${BASE_URL}/invoices/${id}/return`, { comments });
    return response.data;
  },

  /**
   * Get workflow history for an invoice
   */
  getWorkflowHistory: async (id: number): Promise<{ success: boolean; data: WorkflowHistory[] }> => {
    const response = await axiosInstance.get(`${BASE_URL}/invoices/${id}/workflow-history`);
    return response.data;
  },

  /**
   * Add comment to invoice
   */
  addComment: async (
    id: number,
    comment: string,
    commentType?: 'general' | 'review' | 'recommendation' | 'approval'
  ): Promise<{ success: boolean; data: InvoiceComment; message: string }> => {
    const response = await axiosInstance.post(`${BASE_URL}/invoices/${id}/comments`, {
      comment,
      commentType: commentType || 'general',
    });
    return response.data;
  },

  /**
   * Get comments for an invoice
   */
  getComments: async (id: number): Promise<{ success: boolean; data: InvoiceComment[] }> => {
    const response = await axiosInstance.get(`${BASE_URL}/invoices/${id}/comments`);
    return response.data;
  },

  /**
   * Upload document for invoice
   */
  uploadDocument: async (
    id: number,
    file: File,
    documentType: 'signed_invoice' | 'supporting_document' = 'signed_invoice'
  ): Promise<{ success: boolean; data: any; message: string }> => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('documentType', documentType);

    const response = await axiosInstance.post(`${BASE_URL}/invoices/${id}/documents`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  },

  /**
   * List documents for an invoice
   */
  listDocuments: async (id: number): Promise<{ success: boolean; data: any[] }> => {
    const response = await axiosInstance.get(`${BASE_URL}/invoices/${id}/documents`);
    return response.data;
  },

  /**
   * Download document
   */
  downloadDocument: async (id: number, docId: number): Promise<Blob> => {
    const response = await axiosInstance.get(`${BASE_URL}/invoices/${id}/documents/${docId}/download`, {
      responseType: 'blob',
    });
    return response.data;
  },

  /**
   * Delete document
   */
  deleteDocument: async (id: number, docId: number): Promise<{ success: boolean; message: string }> => {
    const response = await axiosInstance.delete(`${BASE_URL}/invoices/${id}/documents/${docId}`);
    return response.data;
  },

    exportInvoices: async (params: {
    exportType: 'all' | 'pending_advance_payment' | 'running_bill' | 'final_bill' | 'first_and_final';
    myInvoicesOnly?: boolean;
    status?: string;
    projectCodeId?: string;
  }): Promise<Blob> => {
    const response = await axiosInstance.get(`${BASE_URL}/export`, {
      params,
      responseType: 'blob',
    });
    return response.data;
  },

};

export default invoiceService;
